/**
 * Algolia Helper Functions
 * 
 * Utility functions for filtering user and listing publicData before saving to Algolia.
 * Uses whitelist approach to ensure only approved fields are indexed.
 */

import {
    ALGOLIA_USER_PUBLIC_DATA_WHITELIST,
    ALGOLIA_LISTING_USER_PUBLIC_DATA_WHITELIST,
} from './algoliaConstants';
import { LISTING_ALOGLIA_REMOVABLE_FIELDS } from './constants';

/**
 * Filters user publicData to only include whitelisted fields for Algolia User Index
 * @param {Object} publicData - User's publicData object
 * @param {Object} additionalFields - Additional fields to include (e.g., { totalListings: 5 })
 * @returns {Object} Filtered publicData with only whitelisted fields
 */
export const filterUserPublicDataForAlgolia = (publicData = {}, additionalFields = {}) => {
    const filtered = {};

    // Include only whitelisted fields
    ALGOLIA_USER_PUBLIC_DATA_WHITELIST.forEach(field => {
        if (publicData.hasOwnProperty(field)) {
            filtered[field] = publicData[field];
        }
    });

    // Merge additional fields (like totalListings which is calculated)
    return { ...filtered, ...additionalFields };
};

/**
 * Filters user publicData to only include whitelisted fields for Algolia Listing Index
 * Used when embedding user data in listing objects
 * @param {Object} publicData - User's publicData object
 * @returns {Object} Filtered publicData with only whitelisted fields
 */
export const filterListingUserPublicDataForAlgolia = (publicData = {}) => {
    const filtered = {};

    ALGOLIA_LISTING_USER_PUBLIC_DATA_WHITELIST.forEach(field => {
        if (publicData.hasOwnProperty(field)) {
            filtered[field] = publicData[field];
        }
    });

    return filtered;
};

/**
 * Filters listing publicData to exclude removable fields for Algolia Listing Index
 * This maintains backward compatibility with the existing exclusion-based approach for listings
 * @param {Object} publicData - Listing's publicData object
 * @param {Array} removeables - Array of field names to exclude (defaults to LISTING_ALOGLIA_REMOVABLE_FIELDS)
 * @returns {Object} Filtered publicData without excluded fields
 */
export const filterPublicDataToAlgolia = (publicData = {}, removeables = LISTING_ALOGLIA_REMOVABLE_FIELDS) => {
    const newObj = {};
    for (let key in publicData) {
        if (!removeables.includes(key)) {
            newObj[key] = publicData[key];
        }
    }
    return newObj;
};

